import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { TONE_ICONS } from './tones';

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

const toneClass: Record<AlertTone, string> = {
  info: 'border-info bg-info-soft',
  warning: 'border-warning bg-warning-soft',
  danger: 'border-danger bg-danger-soft',
  success: 'border-success bg-success-soft',
};
const iconClass: Record<AlertTone, string> = {
  info: 'text-info',
  warning: 'text-warning',
  danger: 'text-danger',
  success: 'text-success',
};
const toneLabel: Record<AlertTone, string> = {
  info: 'Information',
  warning: 'Warning',
  danger: 'Important',
  success: 'Success',
};

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Buttons/links shown under the message. */
  action?: ReactNode;
  onDismiss?: () => void;
  /**
   * Announce to screen readers when it appears: 'assertive' (role=alert, errors) or 'polite' (role=status).
   * Leave unset for static page content.
   */
  live?: 'polite' | 'assertive';
  /** Full-width strip without rounded corners (page-level banner). */
  banner?: boolean;
  className?: string;
}

/** Message box with icon + text in four tones. `<Banner>` is the full-width variant. */
export function Alert({ tone = 'info', title, children, action, onDismiss, live, banner, className }: AlertProps) {
  const Icon = TONE_ICONS[tone];
  const role = live === 'assertive' ? 'alert' : live === 'polite' ? 'status' : undefined;
  return (
    <div
      role={role}
      className={cn(
        'flex gap-3 border-l-[6px] p-4 text-text',
        banner ? 'border-y-0 border-r-0' : 'rounded-lg border',
        toneClass[tone],
        className,
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-0.5 h-6 w-6 shrink-0', iconClass[tone])} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="sr-only">{toneLabel[tone]}: </span>
        {title && <p className="font-bold">{title}</p>}
        {children && <div className="[&_a]:font-bold">{children}</div>}
        {action && <div className="mt-2 flex flex-wrap gap-2">{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss message"
          className="-m-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-surface/60"
        >
          <X aria-hidden="true" className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

export type BannerProps = Omit<AlertProps, 'banner'>;

export function Banner(props: BannerProps) {
  return <Alert {...props} banner />;
}
